/**
 * Display and payload helpers for product enrichment review UI.
 */

const EMPTY_LABEL = '(empty)';

/** Fillable keys sent via POST submit `{ fields }`. */
export const ENRICHMENT_FORM_FIELD_KEYS = [
  'name',
  'manufacturer',
  'category',
  'unit',
  'unit_quantity',
  'units_per_sub_unit',
  'storage',
  'notes',
  'url',
  'expiry_relevant',
  'kit_description',
  'image_url',
];

const NUMERIC_FORM_KEYS = new Set(['unit_quantity', 'units_per_sub_unit']);

/** Keys shown in the extracted specs panel (prefer field_diffs over raw_extracted_json). */
export const EXTRACTED_SPEC_FIELD_KEYS = [
  'catalog_number',
  'applications',
  'reactivity',
  'target',
  'host',
  'datasheet_url',
  'sds_url',
  'cas_number',
  'molecular_weight',
];

/** Submit-only field (not part of form init). */
export const ENRICHMENT_SUBMIT_ONLY_FIELD_KEYS = ['product_image_id'];

/** Accepted MIME types for enrichment image file upload (matches ProductModal). */
export const ENRICHMENT_IMAGE_ACCEPT =
  'image/jpeg,image/jpg,image/png,image/webp,image/gif,image/avif';

/**
 * @param {Object|null|undefined} snapshot
 * @returns {boolean}
 */
export function productSnapshotHasImage(snapshot) {
  return snapshot?.has_image === true;
}

/**
 * Supplier id from enrichment detail (for filtered manufacturer list).
 *
 * @param {Object|null|undefined} detail
 * @returns {string|number|''}
 */
export function getEnrichmentSupplierId(detail) {
  const snap = detail?.product_snapshot;
  if (!snap) return '';
  const supplier = snap.supplier;
  if (supplier != null && typeof supplier === 'object' && supplier.id != null) {
    return supplier.id;
  }
  if (snap.supplier_id != null) return snap.supplier_id;
  if (supplier != null && typeof supplier !== 'object') return supplier;
  return '';
}

/**
 * Suggested OEM manufacturer name from enrichment detail (not select-list id).
 *
 * @param {Object|null|undefined} detail
 * @returns {string}
 */
export function getSuggestedManufacturerName(detail) {
  const suggested = detail?.suggested_form?.manufacturer;
  if (suggested != null && String(suggested).trim() !== '') {
    return String(suggested).trim();
  }
  const oemName = detail?.suggested_manufacturer_name;
  if (oemName != null && String(oemName).trim() !== '') {
    return String(oemName).trim();
  }
  const snapshotMfr = detail?.product_snapshot?.manufacturer;
  if (snapshotMfr == null) return '';
  if (typeof snapshotMfr === 'string') return snapshotMfr.trim();
  if (typeof snapshotMfr === 'object' && snapshotMfr.name) {
    return String(snapshotMfr.name).trim();
  }
  return '';
}

/**
 * Match a manufacturer select-list option by id or label.
 *
 * @param {Array<{value: unknown, label?: string}>} manufacturerList
 * @param {{ id?: unknown, name?: string }} criteria
 * @returns {{ value: unknown, label?: string }|null}
 */
export function findManufacturerOption(manufacturerList, { id, name } = {}) {
  if (!Array.isArray(manufacturerList) || manufacturerList.length === 0) {
    return null;
  }
  if (id != null && String(id).trim() !== '') {
    const byId = manufacturerList.find(m => String(m.value) === String(id));
    if (byId) return byId;
  }
  if (name != null && String(name).trim() !== '') {
    const norm = String(name).trim().toLowerCase();
    return (
      manufacturerList.find(
        m => String(m.label ?? '').trim().toLowerCase() === norm
      ) || null
    );
  }
  return null;
}

/**
 * Manufacturer select value (option id) for the enrichment form.
 *
 * @param {Object|null|undefined} detail
 * @param {Array<{value: unknown, label?: string}>} [manufacturerList]
 * @returns {string}
 */
export function resolveManufacturerFormValue(detail, manufacturerList = []) {
  const snapMfr = detail?.product_snapshot?.manufacturer;
  const snapId =
    typeof snapMfr === 'object' && snapMfr?.id != null ? snapMfr.id : null;
  const name = getSuggestedManufacturerName(detail);
  const match = findManufacturerOption(manufacturerList, { id: snapId, name });
  return match ? String(match.value) : '';
}

/**
 * Build editable form state from detail `suggested_form` + `product_snapshot`.
 *
 * @param {Object|null|undefined} detail
 * @param {Array<{value: unknown, label?: string}>} [manufacturerList]
 * @returns {Object}
 */
export function buildInitialFormValues(detail, manufacturerList = []) {
  const suggested = detail?.suggested_form || {};
  const snapshot = detail?.product_snapshot || {};

  const values = {};
  ENRICHMENT_FORM_FIELD_KEYS.forEach(key => {
    if (key === 'manufacturer') {
      values[key] = resolveManufacturerFormValue(detail, manufacturerList);
      return;
    }
    if (
      Object.prototype.hasOwnProperty.call(suggested, key) &&
      suggested[key] != null &&
      suggested[key] !== ''
    ) {
      values[key] = suggested[key];
      return;
    }
    const snapVal = snapshot[key];
    if (snapVal != null && snapVal !== '') {
      values[key] = snapVal;
      return;
    }
    values[key] = '';
  });
  return values;
}

/**
 * Normalize form values for POST submit.
 *
 * @param {Object} formValues
 * @param {{ manufacturerList?: Array<{value: unknown, label?: string}>, productImageId?: number|null }} [options]
 * @returns {{ fields: Object }}
 */
export function buildSubmitPayload(formValues = {}, options = {}) {
  const { manufacturerList = [], productImageId = null } = options;
  const fields = {};
  ENRICHMENT_FORM_FIELD_KEYS.forEach(key => {
    if (!Object.prototype.hasOwnProperty.call(formValues, key)) return;
    const raw = formValues[key];
    if (raw === null || raw === undefined) return;
    if (typeof raw === 'string' && raw.trim() === '') return;

    if (key === 'manufacturer') {
      const opt = findManufacturerOption(manufacturerList, { id: raw });
      if (opt?.label) {
        fields[key] = String(opt.label).trim();
      } else if (typeof raw === 'string' && raw.trim() !== '') {
        fields[key] = raw.trim();
      }
      return;
    }

    if (NUMERIC_FORM_KEYS.has(key)) {
      const n = Number(raw);
      if (Number.isFinite(n)) {
        fields[key] = n;
      }
      return;
    }
    fields[key] = typeof raw === 'string' ? raw.trim() : raw;
  });
  if (productImageId != null && productImageId !== '') {
    fields.product_image_id = Number(productImageId);
    delete fields.image_url;
  }
  return { fields };
}

/**
 * Build rows for the extracted specs panel (field_diffs preferred over raw JSON).
 *
 * @param {Object|null|undefined} detail
 * @returns {Array<{ key: string, label: string, value: unknown, highlight?: boolean }>}
 */
export function buildExtractedSpecRows(detail) {
  const fieldDiffs = detail?.field_diffs || {};
  const raw = detail?.raw_extracted_json || {};
  const catNum = detail?.product_snapshot?.cat_num;

  return EXTRACTED_SPEC_FIELD_KEYS.map(key => {
    const diff = fieldDiffs[key];
    let value;
    let label = key.replace(/_/g, ' ');

    if (diff) {
      value = diff.suggested ?? diff.current;
      if (diff.label) label = String(diff.label);
    } else if (Object.prototype.hasOwnProperty.call(raw, key)) {
      value = raw[key];
    } else {
      return null;
    }

    const highlight =
      key === 'catalog_number' &&
      catNum != null &&
      value != null &&
      String(value).trim().toLowerCase() !== String(catNum).trim().toLowerCase();

    return { key, label, value, highlight };
  }).filter(Boolean);
}

/**
 * field_diffs entries for read-only table, excluding extracted-spec keys.
 *
 * @param {Object|null|undefined} fieldDiffs
 * @returns {Array<[string, Object]>}
 */
export function getReadOnlyFieldDiffEntries(fieldDiffs) {
  if (!fieldDiffs || typeof fieldDiffs !== 'object') return [];
  const extracted = new Set(EXTRACTED_SPEC_FIELD_KEYS);
  return Object.entries(fieldDiffs).filter(
    ([key, diff]) => (!diff || diff.writable !== true) && !extracted.has(key)
  );
}

/**
 * @param {string[]|null|undefined} appliedFields
 * @returns {string}
 */
export function formatAppliedFieldsMessage(appliedFields) {
  if (!Array.isArray(appliedFields) || appliedFields.length === 0) {
    return 'Enrichment fields applied';
  }
  return `Applied: ${appliedFields.join(', ')}`;
}

/**
 * @param {Object} formValues
 * @param {Object} initialValues
 * @returns {boolean}
 */
export function hasSubmitFormChanges(formValues, initialValues) {
  return ENRICHMENT_FORM_FIELD_KEYS.some(key => {
    const a = formValues?.[key];
    const b = initialValues?.[key];
    return String(a ?? '') !== String(b ?? '');
  });
}

/**
 * @param {*} value
 * @returns {string}
 */
export function formatFieldDisplayValue(value) {
  if (value === null || value === undefined || value === '') {
    return EMPTY_LABEL;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return EMPTY_LABEL;
    return value.map(item => String(item)).join(', ');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

/**
 * Bootstrap badge variant for matched_product_confidence.
 *
 * @param {string} confidence
 * @returns {'success'|'warning'|'danger'|'secondary'}
 */
export function getConfidenceBadgeVariant(confidence) {
  const key = String(confidence || '').toLowerCase();
  if (key === 'high') return 'success';
  if (key === 'medium') return 'warning';
  if (key === 'low') return 'danger';
  return 'secondary';
}

/**
 * @param {Object|null|undefined} fieldDiffs
 * @returns {string[]}
 */
export function getWritableFieldKeys(fieldDiffs) {
  if (!fieldDiffs || typeof fieldDiffs !== 'object') return [];
  return Object.entries(fieldDiffs)
    .filter(([, diff]) => diff && diff.writable === true)
    .map(([key]) => key);
}

/**
 * @deprecated Use buildSubmitPayload for POST submit.
 * @param {Set<string>|string[]} selectedFields
 * @param {Object} editedValues
 * @returns {{ approved_fields: string[], edited_values: Object }}
 */
export function buildApprovePayload(selectedFields, editedValues = {}) {
  const approved_fields = Array.from(selectedFields);
  const edited_values = {};
  approved_fields.forEach(field => {
    if (
      Object.prototype.hasOwnProperty.call(editedValues, field) &&
      editedValues[field] !== undefined &&
      editedValues[field] !== null
    ) {
      edited_values[field] = editedValues[field];
    }
  });
  return { approved_fields, edited_values };
}

/**
 * @param {string} confidence
 * @returns {boolean}
 */
export function shouldShowLowConfidenceBanner(confidence) {
  const key = String(confidence || '').toLowerCase();
  return key === 'low' || key === 'medium';
}

/**
 * Human-readable field label from diff metadata.
 *
 * @param {string} fieldKey
 * @param {Object} [diff]
 * @returns {string}
 */
export function getFieldLabel(fieldKey, diff) {
  if (diff?.label) return String(diff.label);
  return fieldKey.replace(/_/g, ' ');
}

/**
 * @param {string} iso
 * @returns {string}
 */
export function formatEnrichmentCreatedAt(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString();
  } catch {
    return String(iso);
  }
}
